import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import digisakaLogo from '../../../assets/logo-transparent.png';
import {
  formatReportDate,
  formatReportDateRange,
  formatReportDateShort,
  greennessBand,
  type CropCalendarReportModel,
} from './cropCalendarReport';

/** A4 at 96dpi — matches 210mm × 297mm */
const PAGE_W = 794;
const PAGE_H = 1123;
const SIDE_PAD = 57; /* ~15mm */

const CSS = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  :root{
    --g:#0e5a2f; --g2:#126A37; --g3:#e7f1ea; --gold:#b7852b; --gold2:#f6ecd6;
    --ink:#14201a; --mut:#667469; --line:#e4e8e2; --paper:#fff;
  }
  body{
    margin:0; background:#fff; color:var(--ink);
    font:13px/1.5 Inter, system-ui, -apple-system, sans-serif;
    -webkit-print-color-adjust:exact; print-color-adjust:exact;
  }
  .page{
    width:${PAGE_W}px; height:${PAGE_H}px; margin:0; background:var(--paper);
    position:relative; overflow:hidden; padding:0 ${SIDE_PAD}px;
  }
  .serif{ font-family: Fraunces, Georgia, 'Times New Roman', serif; }
  .top{
    margin:0 -${SIDE_PAD}px; min-height:128px; padding:18px ${SIDE_PAD}px;
    background:linear-gradient(120deg,#0a4624,#126A37 60%,#1f8a4b);
    color:#fff; display:flex; align-items:flex-start; justify-content:space-between;
    position:relative;
  }
  .top.short{ height:106px; }
  .top:after{
    content:""; position:absolute; right:-76px; top:-76px; width:265px; height:265px;
    border-radius:50%; border:1px solid rgba(255,255,255,.14);
    box-shadow:0 0 0 53px rgba(255,255,255,.04);
  }
  .brand{ display:flex; gap:10px; align-items:center; z-index:1; }
  .brand-logo{ height:40px; width:auto; object-fit:contain; display:block; flex-shrink:0; }
  .brand b{ font-size:19px; letter-spacing:.01em; display:block; line-height:1.1; }
  .brand span{ font-size:11px; opacity:.8; letter-spacing:.14em; text-transform:uppercase; }
  .meta{ text-align:right; font-size:11px; opacity:.9; z-index:1; line-height:1.45; max-width:340px; }
  .meta b{ font-weight:600; }
  .meta-tagline{
    margin-top:8px; font-size:9px; line-height:1.4; opacity:.88; font-weight:400;
  }
  .page-main{ padding-bottom:82px; }
  .hero{ display:flex; justify-content:space-between; align-items:flex-end; margin:34px 0 15px; }
  h1{ font-size:34px; line-height:1.05; margin:0; font-weight:700; letter-spacing:-.01em; }
  .pg2 h2{ font-size:22px; margin:23px 0 9px; font-weight:700; }
  .fid{ color:var(--mut); font-size:12px; margin-top:3px; }
  .st{
    background:var(--g3); color:var(--g); font-weight:700; font-size:11px;
    letter-spacing:.08em; text-transform:uppercase; padding:5px 12px; border-radius:99px;
    white-space:nowrap;
  }
  .st:before{
    content:""; display:inline-block; width:6px; height:6px; border-radius:50%;
    background:var(--g2); margin-right:7px;
  }
  .tags{ display:flex; gap:6px; flex-wrap:wrap; margin-bottom:0; }
  .tag{ border:1px solid var(--line); border-radius:99px; padding:3px 11px; font-weight:600; font-size:11.5px; }
  .dates{ display:grid; grid-template-columns:repeat(3,1fr); margin:19px 0; border:1px solid var(--line); border-radius:12px; }
  .dates.cols-2{ grid-template-columns:repeat(2,1fr); }
  .dates div{ padding:9px 14px; }
  .dates div+div{ border-left:1px solid var(--line); }
  .dates small{ display:block; color:var(--mut); font-size:10.5px; letter-spacing:.06em; text-transform:uppercase; }
  .dates b{ font-size:14px; display:block; margin-top:2px; font-weight:700; }
  .lbl{ font-size:10.5px; letter-spacing:.14em; text-transform:uppercase; font-weight:700; color:var(--mut); margin:0 0 6px; }
  .card{ border:1px solid var(--line); border-radius:14px; padding:19px 23px; margin-bottom:17px; background:#fff; }
  .now{ display:grid; grid-template-columns:1.1fr 1fr; gap:30px; align-items:center; }
  .stage{ font-size:44px; line-height:1; color:var(--g2); font-weight:700; margin:2px 0 8px; }
  .conf{
    display:inline-flex; gap:8px; align-items:center; font-size:11.5px; font-weight:600;
    background:var(--g3); color:var(--g); padding:4px 11px; border-radius:99px;
  }
  .dots i{ display:inline-block; width:6px; height:6px; border-radius:50%; background:var(--g2); margin-right:2px; }
  .dots i.o{ background:#b9d3c2; }
  .glance{
    background:var(--g); color:#fff; border-radius:10px; padding:10px 14px;
    margin-top:10px; font-weight:500; font-size:12.5px; line-height:1.45;
  }
  .rail{ display:flex; margin:10px 0 4px; gap:3px; }
  .rail i{ flex:1; height:8px; border-radius:5px; background:var(--line); }
  .rail i.d{ background:var(--g2); }
  .rail i.c{ background:linear-gradient(90deg,var(--g2) 55%,var(--line) 55%); }
  .rl{ display:flex; justify-content:space-between; font-size:10.5px; color:var(--mut); gap:4px; }
  .rl b{ color:var(--g2); }
  .hv{ display:grid; grid-template-columns:1fr 1fr; gap:23px; margin-bottom:8px; }
  .hv small{ font-size:10.5px; letter-spacing:.1em; text-transform:uppercase; color:var(--mut); font-weight:700; }
  .hv b{ display:block; font-size:26px; margin-top:2px; font-weight:700; }
  .hv .e b{ color:var(--gold); }
  .gold{ background:linear-gradient(180deg,#fffaf0,var(--gold2)); border-color:#ecdcb4; }
  .pill{
    display:inline-block; background:var(--gold); color:#fff; font-size:10.5px; font-weight:700;
    letter-spacing:.08em; text-transform:uppercase; padding:3px 10px; border-radius:99px; margin-right:6px;
  }
  .pill.ok{ background:#126A37; }
  .pill.later{ background:#1d5fa8; }
  .pill.none{ background:#667469; }
  .two{ display:grid; grid-template-columns:1fr 1fr; gap:17px; margin-bottom:17px; }
  .two .card{ margin:0; }
  .two.notes{ margin-top:0; margin-bottom:0; }
  .tip{ display:flex; gap:10px; margin:7px 0; font-size:12px; line-height:1.45; color:#34443a; }
  .tip i{
    flex:none; width:20px; height:20px; border-radius:50%; background:var(--g3); color:var(--g);
    font:700 11px/20px Inter, sans-serif; text-align:center; font-style:normal;
  }
  .note{ background:#f4f6f3; border-radius:10px; padding:9px 12px; color:var(--mut); font-size:11.5px; line-height:1.45; }
  .foot{
    position:absolute; left:${SIDE_PAD}px; right:${SIDE_PAD}px; bottom:28px;
    border-top:1px solid var(--line); padding-top:8px;
    display:flex; justify-content:space-between; align-items:flex-start; gap:16px;
  }
  .foot-left{ flex:1; min-width:0; }
  .foot-brand{
    font-size:10px; font-weight:700; color:var(--g2); line-height:1.35;
    letter-spacing:.01em;
  }
  .foot-note{
    margin-top:4px; font-size:9px; color:var(--mut); line-height:1.45; max-width:560px;
  }
  .foot-page{
    flex-shrink:0; font-size:10px; color:var(--mut); line-height:1.35; text-align:right;
    padding-top:1px;
  }
  table{ width:100%; border-collapse:collapse; }
  th{
    font-size:10px; letter-spacing:.1em; text-transform:uppercase; color:var(--mut);
    text-align:left; padding:6px 0; border-bottom:1px solid var(--line); font-weight:700;
  }
  td{ padding:7px 4px 7px 0; border-bottom:1px dashed var(--line); font-size:11px; vertical-align:top; }
  tr:last-child td{ border-bottom:none; }
  .tl{ position:relative; padding-left:20px; }
  .tl div{
    position:relative; padding:7px 0; display:flex; justify-content:space-between;
    gap:12px; border-bottom:1px dashed var(--line); font-size:11.5px;
  }
  .tl div:last-child{ border-bottom:none; }
  .tl div:before{
    content:""; position:absolute; left:-20px; top:13px; width:9px; height:9px;
    border-radius:50%; background:#b9d3c2;
  }
  .tl div.n:before{ background:var(--g2); box-shadow:0 0 0 4px var(--g3); }
  .tl div.n{ font-weight:700; }
  .tl em{ font-style:normal; color:var(--mut); font-weight:400; white-space:nowrap; }
  .bar{ display:inline-block; width:60px; height:6px; background:var(--line); border-radius:4px; vertical-align:middle; margin-right:6px; }
  .bar i{ display:block; height:100%; background:var(--g2); border-radius:4px; }
  svg text{ font:10px Inter, sans-serif; fill:#667469; }
  .bodytext{ margin:0; color:#34443a; font-size:12.5px; line-height:1.5; }
  .next-title{ font-size:19px; display:block; margin-bottom:4px; font-weight:700; }
  .next-when{ color:var(--g2); font-weight:600; margin:2px 0 6px; font-size:12.5px; }
  .harvest-line{ margin:2px 0 0; font-size:12.5px; line-height:1.45; color:#34443a; }
`;

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap';

const CROP_PDF_FOOTER_NOTE =
  'Greenness and crop stage values are field estimates, not guarantees. Confirm conditions on site before acting.';

const CROP_PDF_HEADER_TAGLINE =
  'See the next 5 days on this field: rain, heat, and wind, where drought or pests need attention, and the best days to spray, water, or work the field.';

function cropReportId(farmId: string, preparedIso: string): string {
  const day = preparedIso.replace(/-/g, '');
  return `CC-${farmId || 'X'}-${day}`;
}

function reportFooter(reportId: string, page: number, total: number): string {
  return `<div class="foot">
    <div class="foot-left">
      <div class="foot-brand">Digisaka: Spatial Intelligence Platform · ${esc(reportId)}</div>
      <div class="foot-note">${esc(CROP_PDF_FOOTER_NOTE)}</div>
    </div>
    <div class="foot-page">Page ${page} of ${total}</div>
  </div>`;
}

function esc(value: string | null | undefined): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function parseIso(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function statusLabel(status: string): string {
  if (!status) return 'Ongoing';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function seasonLabel(season: string): string {
  if (!season) return '';
  return /season|cropping/i.test(season) ? season : `${season} cropping`;
}

function confidenceDots(howSure: string): { filled: number; label: string } {
  const s = howSure.toLowerCase();
  if (/high|very|strong/.test(s)) return { filled: 3, label: howSure };
  if (/fair|moderate|mid|sure/.test(s)) return { filled: 2, label: howSure };
  if (/low|limited|uncertain/.test(s)) return { filled: 1, label: howSure };
  return { filled: 2, label: howSure || 'Fairly sure' };
}

function confidenceHtml(howSure: string): string {
  const { filled, label } = confidenceDots(howSure);
  const dots = [0, 1, 2]
    .map((i) => `<i class="${i < filled ? '' : 'o'}"></i>`)
    .join('');
  return `<span class="conf"><span class="dots">${dots}</span>${esc(label)}</span>`;
}

function seasonRailHtml(periods: CropCalendarReportModel['stagePeriods']): string {
  if (!periods.length) return '';
  const pick = periods.length > 5 ? periods.slice(-5) : periods;
  const currentIdx = pick.findIndex((p) => p.isCurrent);
  const rail = pick
    .map((p, i) => {
      const done = currentIdx >= 0 && i < currentIdx;
      const current = p.isCurrent;
      const cls = done ? 'd' : current ? 'c' : '';
      return `<i class="${cls}"></i>`;
    })
    .join('');
  const labels = pick
    .map((p) => {
      const short = p.stage.split(' ')[0];
      if (p.isCurrent) return `<span><b>${esc(p.stage)} · now</b></span>`;
      return `<span>${esc(short)}</span>`;
    })
    .join('');
  return `<div class="rail">${rail}</div><div class="rl">${labels}</div>`;
}

function harvestPillClass(kind: CropCalendarReportModel['harvest']['kind']): string {
  if (kind === 'on_track') return 'ok';
  if (kind === 'later') return 'later';
  if (kind === 'earlier') return '';
  return 'none';
}

function harvestTimelineSvg(report: CropCalendarReportModel): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const predicted = parseIso(report.predictedHarvestDate);
  const expected = parseIso(report.expectedHarvestDate);
  const points = [
    { d: today, kind: 'today' as const },
    predicted ? { d: predicted, kind: 'estimate' as const } : null,
    expected ? { d: expected, kind: 'plan' as const } : null,
  ].filter(Boolean) as Array<{ d: Date; kind: 'today' | 'estimate' | 'plan' }>;

  if (points.length < 2) return '';

  const min = Math.min(...points.map((p) => p.d.getTime()));
  const max = Math.max(...points.map((p) => p.d.getTime()));
  const pad = Math.max(86400000 * 7, (max - min) * 0.15);
  const start = min - pad;
  const end = max + pad;
  const span = end - start || 1;
  const xFor = (d: Date) => 10 + ((d.getTime() - start) / span) * 500;

  const todayX = xFor(today);
  const estX = predicted ? xFor(predicted) : null;
  const planX = expected ? xFor(expected) : null;

  let dash = '';
  if (estX != null && planX != null) {
    const left = Math.min(estX, planX);
    const right = Math.max(estX, planX);
    dash = `<line x1="${left}" x2="${right}" y1="26" y2="26" stroke="#b7852b" stroke-width="4" stroke-dasharray="2 5"/>`;
  }

  return `<svg viewBox="0 0 520 54" width="100%" role="img" aria-label="Harvest timeline">
    <line x1="10" x2="510" y1="26" y2="26" stroke="#e2d4ae" stroke-width="4" stroke-linecap="round"/>
    ${dash}
    <circle cx="${todayX}" cy="26" r="5" fill="#126A37"/>
    <text x="${Math.max(8, todayX - 16)}" y="46">Today</text>
    ${
      estX != null
        ? `<circle cx="${estX}" cy="26" r="9" fill="#b7852b"/>
           <text x="${Math.max(8, estX - 18)}" y="12">${esc(formatReportDateShort(report.predictedHarvestDate))}</text>
           <text x="${Math.max(8, estX - 22)}" y="46">Estimate</text>`
        : ''
    }
    ${
      planX != null
        ? `<circle cx="${planX}" cy="26" r="9" fill="#fff" stroke="#14201a" stroke-width="3"/>
           <text x="${Math.max(8, planX - 14)}" y="12">${esc(formatReportDateShort(report.expectedHarvestDate))}</text>
           <text x="${Math.max(8, planX - 10)}" y="46">Plan</text>`
        : ''
    }
  </svg>`;
}

function greennessChartSvg(
  rows: CropCalendarReportModel['greennessRows'],
  stagePeriods: CropCalendarReportModel['stagePeriods']
): string {
  if (rows.length < 2) return '';

  const w = 560;
  const h = 190;
  const left = 30;
  const right = 530;
  const plotTop = 14;
  const plotBottom = 144;
  const plotW = right - left;
  const pts = rows.map((r) => r.ndvi);
  const px = (i: number) => left + i * (plotW / Math.max(pts.length - 1, 1));
  const py = (v: number) => plotBottom - v * (plotBottom - plotTop);
  const line = pts.map((v, i) => `${px(i)},${py(v)}`).join(' ');

  const stages = stagePeriods.filter((p) => p.stage);
  const bandCount = Math.min(4, Math.max(1, stages.length));
  const bands: string[] = [];
  if (pts.length >= 2 && bandCount >= 1) {
    for (let i = 0; i < bandCount; i += 1) {
      const a = Math.floor((i / bandCount) * (pts.length - 1));
      const b = Math.ceil(((i + 1) / bandCount) * (pts.length - 1));
      const label = stages[Math.min(i, stages.length - 1)]?.stage || '';
      const opacity = i % 2 ? 0.05 : 0.08 + i * 0.02;
      bands.push(
        `<rect x="${px(a)}" y="${plotTop}" width="${Math.max(0, px(b) - px(a))}" height="${plotBottom - plotTop}" fill="#126A37" opacity="${opacity}"/>` +
          (label ? `<text x="${px(a) + 5}" y="28">${esc(label.split(' ')[0])}</text>` : '')
      );
    }
  }

  const pointMarkup = rows
    .map((row, i) => {
      const x = px(i);
      const y = py(row.ndvi);
      return `<circle cx="${x}" cy="${y}" r="6" fill="#fff" stroke="#126A37" stroke-width="3"/>
        <text x="${x - 10}" y="${y - 12}" style="fill:#14201a;font-weight:600">${row.ndvi.toFixed(2)}</text>
        <text x="${x - 14}" y="166">${esc(formatReportDateShort(row.date))}</text>`;
    })
    .join('');

  return `<svg viewBox="0 0 ${w} ${h}" width="100%" role="img" aria-label="Greenness through the season">
    ${bands.join('')}
    <g stroke="#e4e8e2"><line x1="${left}" x2="${right}" y1="${plotBottom}" y2="${plotBottom}"/></g>
    <path d="M${left} ${plotBottom} L ${line} L ${px(pts.length - 1)} ${plotBottom}Z" fill="#126A37" opacity=".12"/>
    <polyline points="${line}" fill="none" stroke="#126A37" stroke-width="3" stroke-linejoin="round"/>
    ${pointMarkup}
  </svg>`;
}

function splitTip(tip: string): { nextBody: string; actionTips: string[] } {
  const parts = tip
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 1) return { nextBody: '', actionTips: parts.length ? [parts[0]] : [] };
  return { nextBody: parts[0], actionTips: parts.slice(1).slice(0, 3) };
}

function tipsHtml(tips: string[]): string {
  if (!tips.length) {
    return '<p class="bodytext" style="color:var(--mut)">No tips for this week.</p>';
  }
  return tips
    .map((text, i) => `<div class="tip"><i>${i + 1}</i><span>${esc(text)}</span></div>`)
    .join('');
}

function cloudPctForDate(
  date: string,
  rows: CropCalendarReportModel['greennessRows']
): number | null {
  const row = rows.find((r) => r.date === date);
  return row?.cloudPct ?? null;
}

async function loadLogoDataUrl(): Promise<string> {
  try {
    const res = await fetch(digisakaLogo);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('logo read failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

function brandHeader(logoSrc: string): string {
  const logo = logoSrc
    ? `<img class="brand-logo" src="${logoSrc}" alt="Digisaka" />`
    : '';
  return `<div class="brand">${logo}<div><b class="serif">Digisaka</b><span>Crop report</span></div></div>`;
}

function buildReportHtml(report: CropCalendarReportModel, logoSrc = ''): string {
  const now = new Date();
  const preparedIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const prepared = formatReportDate(preparedIso);
  const farmId = report.farmId || '—';
  const reportId = cropReportId(String(report.farmId || ''), preparedIso);
  const season = seasonLabel(report.croppingSeason);
  const harvest = report.harvest;
  const { nextBody, actionTips } = splitTip(report.tip);

  const dateCells: string[] = [];
  if (report.sowingDate) {
    dateCells.push(`<div><small>Sown</small><b>${esc(formatReportDate(report.sowingDate))}</b></div>`);
  }
  if (report.plantingDate) {
    dateCells.push(
      `<div><small>Planted in the field</small><b>${esc(formatReportDate(report.plantingDate))}</b></div>`
    );
  }
  if (!report.sowingDate && !report.plantingDate && report.establishmentDate) {
    dateCells.push(
      `<div><small>Field start</small><b>${esc(formatReportDate(report.establishmentDate))}</b></div>`
    );
  }
  if (report.endDate) {
    dateCells.push(
      `<div><small>Viewed through</small><b>${esc(formatReportDate(report.endDate))}</b></div>`
    );
  }
  const datesClass = dateCells.length === 2 ? 'dates cols-2' : 'dates';

  const timeline =
    report.stagePeriods.length === 0
      ? '<p class="bodytext">No stage timeline for this run.</p>'
      : `<div class="tl">${report.stagePeriods
          .map((p) => {
            const range = formatReportDateRange(p.start, p.end || null);
            const ongoing = !p.end ? ' – ongoing' : '';
            return `<div class="${p.isCurrent ? 'n' : ''}">${esc(p.stage)}<em>${esc(`${range}${ongoing}`)}</em></div>`;
          })
          .join('')}</div>`;

  const observations =
    report.observations.length === 0
      ? '<p class="bodytext">No dated observations for this run.</p>'
      : `<table><tr><th>Date</th><th>Stage</th><th>What we saw</th><th>Greenness</th><th>Sky cover</th><th>Confidence</th></tr>${report.observations
          .map((row) => {
            const band = greennessBand(row.ndvi);
            const cloud = cloudPctForDate(row.date, report.greennessRows);
            return `<tr>
              <td><b>${esc(formatReportDateShort(row.date))}</b></td>
              <td>${esc(row.stage || '—')}</td>
              <td>${esc(row.label || '—')}</td>
              <td>${row.ndvi.toFixed(2)}</td>
              <td>${cloud == null ? '—' : `${Math.round(cloud)}%`}</td>
              <td><span class="bar"><i style="width:${band.pct}%"></i></span>${band.label}</td>
            </tr>`;
          })
          .join('')}</table>
        <div class="note" style="margin-top:8px">Sky cover can hide the crop. Days with more cloud are less reliable.</div>`;

  const greennessChart =
    report.greennessRows.length >= 2
      ? `${greennessChartSvg(report.greennessRows, report.stagePeriods)}
         <div class="note" style="margin-top:2px">Higher numbers mean healthier green cover. Only the clearest readings are shown.</div>`
      : '<p class="bodytext">Not enough clear readings to draw a chart yet.</p>';

  const planVal = harvest.expectedLabel || '—';
  const predVal = harvest.predictedLabel || 'Not ready yet';

  const noteRow =
    report.greennessTrend.trim() || report.weatherNow
      ? `<div class="two notes">
      ${report.greennessTrend.trim() ? `<div class="note">🌿 ${esc(report.greennessTrend)}</div>` : ''}
      ${report.weatherNow ? `<div class="note">🌦 ${esc(report.weatherNow)}</div>` : ''}
    </div>`
      : '';

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<link href="${FONT_LINK}" rel="stylesheet">
<style>${CSS}</style></head><body>

<section class="page">
  <div class="top">
    ${brandHeader(logoSrc)}
    <div class="meta">
      Prepared <b>${esc(prepared)}</b><br>For your records
      <div class="meta-tagline">${esc(CROP_PDF_HEADER_TAGLINE)}</div>
    </div>
  </div>
  <div class="page-main">
    <div class="hero">
      <div><h1 class="serif">${esc(report.farmName)}</h1><div class="fid">Field ID ${esc(farmId)}</div></div>
      <span class="st">${esc(statusLabel(report.cropStatus))}</span>
    </div>
    <div class="tags">
      ${report.cropType ? `<span class="tag">${esc(report.cropType)}</span>` : ''}
      ${report.plantingMethod ? `<span class="tag">${esc(report.plantingMethod)}</span>` : ''}
      ${season ? `<span class="tag">${esc(season)}</span>` : ''}
    </div>
    ${dateCells.length ? `<div class="${datesClass}">${dateCells.join('')}</div>` : ''}

    <div class="card">
      <div class="now">
        <div>
          <p class="lbl">Where your crop is now</p>
          <div class="stage serif">${esc(report.whereNow || 'Not ready yet')}</div>
          ${report.howSure ? confidenceHtml(report.howSure) : ''}
        </div>
        <p class="bodytext">${esc(report.inPlainWords || '')}</p>
      </div>
      ${seasonRailHtml(report.stagePeriods)}
      ${report.summary ? `<div class="glance">At a glance: ${esc(report.summary)}</div>` : ''}
    </div>

    ${
      harvest.kind !== 'none'
        ? `<div class="card gold">
      <p class="lbl" style="color:var(--gold)">Harvest · plan vs field estimate</p>
      <div class="hv">
        <div><small>Your plan</small><b class="serif">${esc(planVal)}</b></div>
        <div class="e"><small>Field estimate</small><b class="serif">${esc(predVal)}</b></div>
      </div>
      ${harvestTimelineSvg(report)}
      <p class="harvest-line"><span class="pill ${harvestPillClass(harvest.kind)}">${esc(harvest.headline)}</span>${esc(harvest.detail)}</p>
    </div>`
        : ''
    }

    <div class="two">
      <div class="card">
        <p class="lbl">What comes next</p>
        <b class="serif next-title">${esc(report.whatNext || '—')}</b>
        ${report.aboutWhen ? `<div class="next-when">${esc(report.aboutWhen)}</div>` : ''}
        ${nextBody ? `<div class="bodytext">${esc(nextBody)}</div>` : ''}
      </div>
      <div class="card">
        <p class="lbl">Do this now</p>
        ${tipsHtml(actionTips.length ? actionTips : report.tip ? [report.tip] : [])}
      </div>
    </div>

    ${noteRow}
  </div>
  ${reportFooter(reportId, 1, 2)}
</section>

<section class="page pg2">
  <div class="top short">
    <div class="brand"><div><b class="serif" style="font-size:22px">Field readings</b><span>${esc(report.farmName)} · ${esc(farmId)}</span></div></div>
    <div class="meta">Prepared <b>${esc(prepared)}</b><br>${esc(reportId)}</div>
  </div>
  <div class="page-main">
    <h2 class="serif">Greenness through the season</h2>
    <div class="card">${greennessChart}</div>

    <h2 class="serif">Stage timeline</h2>
    <div class="card">${timeline}</div>

    <h2 class="serif">Observations by date</h2>
    <div class="card">${observations}</div>
  </div>
  ${reportFooter(reportId, 2, 2)}
</section>

</body></html>`;
}

async function waitForFonts(): Promise<void> {
  try {
    if (document.fonts?.load) {
      await Promise.all([
        document.fonts.load('700 34px Fraunces'),
        document.fonts.load('700 22px Fraunces'),
        document.fonts.load('400 13px Inter'),
        document.fonts.load('600 11px Inter'),
      ]);
    }
    await document.fonts?.ready;
  } catch {
    /* ignore */
  }
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        img.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
    )
  );
}

export async function downloadCropCalendarPdf(report: CropCalendarReportModel): Promise<void> {
  const logoSrc = await loadLogoDataUrl();
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${PAGE_W}px;background:#fff;z-index:-1;`;
  host.innerHTML = buildReportHtml(report, logoSrc);
  document.body.appendChild(host);
  try {
    await waitForFonts();
    await waitForImages(host);
    await new Promise((r) => setTimeout(r, 600));
    const pages = Array.from(host.querySelectorAll<HTMLElement>('.page'));
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    for (let i = 0; i < pages.length; i += 1) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        width: PAGE_W,
        height: PAGE_H,
        windowWidth: PAGE_W,
        windowHeight: PAGE_H,
      });
      const img = canvas.toDataURL('image/png');
      if (i > 0) pdf.addPage();
      pdf.addImage(img, 'PNG', 0, 0, 210, 297);
    }
    const safeName = (report.farmName || 'farm').replace(/[^\w\-]+/g, '_').slice(0, 40);
    pdf.save(`${safeName}_crop_report.pdf`);
  } finally {
    host.remove();
  }
}
